import React, { useState } from "react";
import {
  COLLECTION_ID,
  FileEntity,
  KNOCK_WORKFLOW,
  Track,
} from "@highnote/server/src/core/entities";
import {
  APP_FEATURES,
  AppFeaturesStatus,
} from "@highnote/server/src/core/features";
import {
  getDefaultVersionId,
  getPinnedVersionsLimit,
  PERMISSION,
} from "@highnote/server/src/core/shared-util";
import { highnote } from "@highnote/server/src/sdk";
import { MenuProps } from "@mui/material";
import {
  ActionDivider,
  ActionInfo,
  ActionItem,
  Actions,
} from "App/common/Actions";
import { ReactComponent as DownloadSVG } from "App/common/icons-v2/download.svg";
import { ReactComponent as EditSVG } from "App/common/icons-v2/edit.svg";
import { ReactComponent as UnpinSVG } from "App/common/icons-v2/no-pushpin.svg";
import { ReactComponent as SwapSVG } from "App/common/icons-v2/refresh-line.svg";
import { ReactComponent as StarFilledSVG } from "App/common/icons-v2/star-fill.svg";
import { ReactComponent as InfoSVG } from "App/common/icons/info.svg";
import { ReactComponent as PinSVG } from "App/common/icons/pin.svg";
import { ReactComponent as MoveSVG } from "App/common/icons-v2/move.svg";
import { ReactComponent as RemoveSVG } from "App/common/icons/remove.svg";
import { useFileDownload } from "App/common/useFileDownload";
import { useHighnote } from "App/common/useHighnote";
import { ConnectedUserName } from "App/common/UserAvatar/UserAvatar";
import { useSpaceContext } from "App/common/useSpace";
import { useAuth } from "App/components/Auth";
import { FILE_EDITOR_FIELD, FileEditor } from "App/components/FileEditor";
import { useTrack, useTrackFiles } from "App/components/useEntities/useTrack";
import { ButtonV2Props } from "App/core/ButtonV2";
import { useTrackVersionEditorContext } from "App/components/TrackVersionEditor/utils/context/TrackVersionEditorContext";
import { useToast } from "App/common/useToast";
import { useVersionActions } from "App/components/TrackVersionEditor/utils/hooks/useVersionActions";
import { useMoveSelectionsToTarget } from "App/components/useEntities/useMoveSelectionsToTarget";

export enum TRACK_VERSION_ACTIONS {
  DOWNLOAD = "DOWNLOAD",
  RENAME = "RENAME",
  SET_AS_DEFAULT = "SET_AS_DEFAULT",
  REMOVE = "REMOVE",
  PIN = "PIN",
  SWAP = "SWAP",
  MOVE = "MOVE",
}

export const DEFAULT_TRACK_VERSION_ACTIONS = [
  TRACK_VERSION_ACTIONS.DOWNLOAD,
  TRACK_VERSION_ACTIONS.RENAME,
  TRACK_VERSION_ACTIONS.SET_AS_DEFAULT,
  TRACK_VERSION_ACTIONS.REMOVE,
  TRACK_VERSION_ACTIONS.PIN,
  TRACK_VERSION_ACTIONS.MOVE,
];

export const ALT_TRACK_VERSION_ACTIONS = [
  TRACK_VERSION_ACTIONS.DOWNLOAD,
  TRACK_VERSION_ACTIONS.RENAME,
  TRACK_VERSION_ACTIONS.MOVE,
];

const SetAsDefaultVersion = ({ version }: { version: FileEntity }) => {
  const { setAsDefaultVersion } = useHighnote();
  const { track, setCurrentTrackVersion } = useTrack();
  const [isDefaultChangeLoading, setIsDefaultChangeLoading] = useState(false);
  const defaultTrackVersionId = getDefaultVersionId(track);
  const isDefaultVersion = defaultTrackVersionId === version.id;

  // Don't show the action if this is already the default version
  if (isDefaultVersion) return null;

  return (
    <ActionItem
      action={{
        icon: <StarFilledSVG />,
        name: `Set as Default Version`,
        onClick: async () => {
          setIsDefaultChangeLoading(true);
          await setAsDefaultVersion({ track, versionId: version.id });
          setCurrentTrackVersion(version);
          setIsDefaultChangeLoading(false);
        },
        disabled: isDefaultChangeLoading,
      }}
    />
  );
};

// Memoize to prevent unnecessary re-renders when parent components update their row configurations.
// This is critical for performance since version data is passed as props and used in multiple
// table contexts that frequently refresh their state.
export const TrackVersionActions = React.memo(
  ({
    actions = [...DEFAULT_TRACK_VERSION_ACTIONS],
    version,
    menuProps,
    track,
    actionButtonProps,
    hideTriggerButton,
  }: {
    actions?: TRACK_VERSION_ACTIONS[];
    version: FileEntity;
    track: Track;
    menuProps?: Partial<MenuProps>;
    actionButtonProps?: ButtonV2Props;
    hideTriggerButton?: boolean;
  }) => {
    const { addErrorMessage } = useToast();
    // The track page's own answer to "is the canonical document out of this
    // viewer's reach?". `useTrack` has a default context value, so this is
    // safe wherever these actions render. That default is only ever
    // correct because the ONE site that enables SWAP, `TrackVersionTabs.tsx`,
    // renders inside a `TrackContextProvider` — if SWAP is ever added to a
    // list context (no provider), the default `isBorrowed: false` would
    // silently attach the canonical watcher for what is actually a
    // borrowed track.
    const { isBorrowed } = useTrack();
    const {
      openTrackVersionEditor,
      setLocalPinnedTrackVersions,
      setLocalTrackVersions,
      viewingSpaceId,
    } = useTrackVersionEditorContext();
    const {
      deleteSelectedVersions,
      renderConfirmation: renderDeleteConfirmation,
    } = useVersionActions();
    const [fileEditorOpen, setFileEditorOpen] = useState(false);
    const { isAllowed } = useAuth();
    const { openMoveSelectionsToTarget } = useMoveSelectionsToTarget();
    const { space, spaceArtworkUrl } = useSpaceContext();
    const {
      versions: trackVersions,
      artworkUrl: trackArtworkUrl,
      refetchTrackFiles,
    } = useTrackFiles(track, viewingSpaceId);
    const pinnedTrackVersions = track?.pinnedVersionFiles;
    const { downloadFile, isDownloading } = useFileDownload();
    const { pinTrackVersion, unpinTrackVersion } = useHighnote();
    const canEditFile = isAllowed(PERMISSION.TO_EDIT_FILE, { file: version });
    const canAddToTrack = isAllowed(PERMISSION.TO_ADD_TO_TRACK_IN_SPACE, {
      space,
      track,
    });
    const canEditTrack = isAllowed(PERMISSION.TO_EDIT_TRACK_IN_SPACE, {
      space,
      track,
    });
    const canDownload = isAllowed(PERMISSION.TO_DOWNLOAD_TRACK_IN_SPACE, {
      track,
      space,
    });

    const isCurrentlyPinned = pinnedTrackVersions?.includes(version.id);
    const isOnlyPin = isCurrentlyPinned && pinnedTrackVersions?.length === 1;
    const isOnlyVersion = trackVersions.length === 1;
    const isDefaultVersion = version.id === getDefaultVersionId(track);

    const menuActions = [];

    if (canDownload) {
      actions.includes(TRACK_VERSION_ACTIONS.DOWNLOAD) &&
        menuActions.push({
          icon: <DownloadSVG />,
          name: "Download",
          disabled: isDownloading,
          disabledReason: "We're processing this download.",
          onClick: async () => {
            await downloadFile(version.id);
            highnote.notify({
              workflowId: KNOCK_WORKFLOW.TRACK_VERSION_DOWNLOADED,
              data: {
                files: [
                  {
                    fileId: version.id,
                    fileName: version.name,
                    versionNumber: track.versionFilesV2.indexOf(version.id) + 1,
                  },
                ],
                artworkUrl: spaceArtworkUrl,
                spaceId: space?.id,
                spaceName: space?.name,
                trackId: track.id,
                trackName: track.title,
                trackArtworkUrl: trackArtworkUrl,
              },
            });
          },
        });
    }

    if (canAddToTrack) {
      actions.includes(TRACK_VERSION_ACTIONS.SET_AS_DEFAULT) &&
        !isDefaultVersion &&
        menuActions.push({
          Component: () => <SetAsDefaultVersion version={version} />,
        });

      actions.includes(TRACK_VERSION_ACTIONS.PIN) &&
        !isOnlyPin &&
        !isDefaultVersion &&
        menuActions.push({
          Component: () => (
            <ActionItem
              action={{
                icon: isCurrentlyPinned ? <UnpinSVG /> : <PinSVG />,
                name: `${isCurrentlyPinned ? "Unpin" : "Pin"}`,
                onClick: async () => {
                  if (isCurrentlyPinned) {
                    const previousPinnedTrackVersions = [
                      ...pinnedTrackVersions,
                    ];
                    setLocalPinnedTrackVersions(
                      pinnedTrackVersions.filter((id) => id !== version.id),
                    );

                    try {
                      await unpinTrackVersion({
                        track,
                        versionIds: [version.id],
                      });
                    } catch (error) {
                      console.error(error);
                      // Revert the pinned versions list
                      setLocalPinnedTrackVersions(previousPinnedTrackVersions);
                      addErrorMessage("Could not unpin version");
                    }
                    return;
                  }

                  // Calculate new pinned versions with sliding window behavior
                  const currentPinnedVersions = pinnedTrackVersions || [];
                  const maxPinnedVersions = getPinnedVersionsLimit(
                    space.subscriptionTier,
                  );

                  // Add the new version to the end
                  let newPinnedVersions = [
                    ...currentPinnedVersions,
                    version.id,
                  ];

                  // If we exceed the limit, remove the oldest pinned version (first in array)
                  if (newPinnedVersions.length > maxPinnedVersions) {
                    newPinnedVersions =
                      newPinnedVersions.slice(-maxPinnedVersions);
                  }

                  // Store original state for rollback
                  const previousPinnedTrackVersions = [...pinnedTrackVersions];

                  // Optimistically update local state
                  setLocalPinnedTrackVersions(newPinnedVersions);

                  try {
                    await pinTrackVersion({
                      track,
                      versionId: version.id,
                      subscriptionTier: space.subscriptionTier,
                    });
                  } catch (error) {
                    console.error(error);
                    // Revert the pinned versions list on error
                    setLocalPinnedTrackVersions(previousPinnedTrackVersions);
                    addErrorMessage("Could not pin version");
                  }
                },
              }}
            />
          ),
        });

      if (AppFeaturesStatus[APP_FEATURES.TRACK_VERSION_EDITOR]) {
        actions.includes(TRACK_VERSION_ACTIONS.SWAP) &&
          isCurrentlyPinned &&
          menuActions.push({
            Component: () => (
              <ActionItem
                action={{
                  icon: <SwapSVG />,
                  name: "Swap",
                  onClick: () => {
                    openTrackVersionEditor({
                      type: "swap",
                      track,
                      isBorrowed,
                      selectedVersionToSwap: version,
                      viewingSpaceId,
                    });
                  },
                }}
              />
            ),
          });
      }
    }

    if (canEditFile) {
      actions.includes(TRACK_VERSION_ACTIONS.RENAME) &&
        menuActions.push({
          icon: <EditSVG />,
          name: "Rename",
          onClick: () => {
            setFileEditorOpen(true);
          },
        });
    }

    if (canEditTrack && !isDefaultVersion && !isOnlyVersion) {
      actions.includes(TRACK_VERSION_ACTIONS.REMOVE) &&
        menuActions.push({
          icon: <RemoveSVG />,
          name: "Remove from Track",
          onClick: async () => {
            await deleteSelectedVersions({
              track,
              versionsToRemove: [
                {
                  entityId: version.id,
                  entityType: COLLECTION_ID.FILE,
                  parentEntity: {
                    entityId: track.id,
                    entityType: COLLECTION_ID.TRACK,
                  },
                },
              ],
              localTrackVersions: trackVersions,
              localPinnedTrackVersions: track.pinnedVersionFiles,
              setLocalTrackVersions,
              setLocalPinnedTrackVersions,
            });
          },
        });
    }

    if ((canEditTrack || canEditFile) && !isOnlyVersion && !isDefaultVersion) {
      actions.includes(TRACK_VERSION_ACTIONS.MOVE) &&
        menuActions.push({
          icon: <MoveSVG />,
          name: "Move to...",
          onClick: () =>
            openMoveSelectionsToTarget({
              selectedEntities: {
                [version.id]: {
                  entityId: version.id,
                  entityType: COLLECTION_ID.FILE,
                  parentEntity: {
                    entityId: track.id,
                    entityType: COLLECTION_ID.TRACK,
                  },
                },
              },
            }),
        });
    }

    if (!canEditFile && menuActions.length > 0) {
      menuActions.push(ActionDivider, {
        Component: () => (
          <ActionInfo>
            <InfoSVG /> Added by&nbsp;
            <ConnectedUserName userId={version.createdBy} />
          </ActionInfo>
        ),
        disabled: true,
      });
    }

    if (!menuActions.length) {
      return null;
    }

    return (
      <>
        {renderDeleteConfirmation}
        {fileEditorOpen && (
          <FileEditor
            file={version}
            title="Rename Version"
            fields={[FILE_EDITOR_FIELD.NAME]}
            onSave={refetchTrackFiles}
            onClose={() => setFileEditorOpen(false)}
          />
        )}
        <Actions
          buttonProps={actionButtonProps}
          actions={menuActions}
          menuProps={menuProps}
          hideTriggerButton={hideTriggerButton}
        />
      </>
    );
  },
);

TrackVersionActions.displayName = "TrackVersionActions";
